import { lazy, useEffect, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * A route page loaded on demand, with a way to start loading it early.
 *
 * Every page is its own chunk, so the first load of the app downloads the frame and Home and
 * nothing else — a company switch lands on a new subdomain with a cold cache, and every byte of
 * a page nobody opened was a byte between the click and the dashboard. `preload()` is what the
 * menu calls on hover/focus (see `preloadRoute`), so the chunk is usually in before the click.
 *
 * `path` is the URL prefix the page lives under, used only to match a menu link to the pages it
 * leads to. Several pages may share a prefix (everything under `/leave`); preloading all of them
 * is fine — `import()` is cached, and they are small.
 */
type AnyComponent = ComponentType<any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export type LazyPage<C extends AnyComponent> = LazyExoticComponent<C> & {
  preload: () => Promise<unknown>;
};

const registry: Array<{ path: string; preload: () => Promise<unknown> }> = [];

export function lazyPage<M, K extends keyof M>(
  path: string | null,
  load: () => Promise<M>,
  name: K,
): LazyPage<M[K] extends AnyComponent ? M[K] : never> {
  type C = M[K] extends AnyComponent ? M[K] : never;
  const Component = lazy(async () => ({ default: (await load())[name] as unknown as C }));
  const page = Object.assign(Component, { preload: load });
  if (path) registry.push({ path, preload: load });
  return page;
}

/**
 * Start loading every page a menu link can lead to. Matches in both directions: `/leave` warms
 * `/leave/calendar`, and `/admin/settings/modules` warms the `/admin` area. Errors are swallowed —
 * a failed preload is retried by the real navigation, which is the one that reports it.
 */
export function preloadRoute(to: string): void {
  const path = to.split(/[?#]/)[0] ?? to;
  for (const entry of registry) {
    if (path === entry.path || path.startsWith(`${entry.path}/`) || entry.path.startsWith(`${path}/`)) {
      entry.preload().catch(() => undefined);
    }
  }
}

/**
 * Warm a page's chunk the moment a pointer, focus or touch reaches a link to it — anywhere in the
 * app: the sidebar, its portalled flyouts, the mobile drawer, a link inside a page. One delegated
 * listener rather than a handler on every `NavLink`, so a new menu or link needs nothing. Each path
 * is warmed once per mount; mounted by the app frame, which every such link renders inside.
 */
export function useLinkIntentPreload(): void {
  useEffect(() => {
    const warmed = new Set<string>();
    const onIntent = (event: Event) => {
      const target = event.target;
      const anchor = target instanceof Element ? target.closest('a[href]') : null;
      if (!(anchor instanceof HTMLAnchorElement) || anchor.origin !== window.location.origin) return;
      if (warmed.has(anchor.pathname)) return;
      warmed.add(anchor.pathname);
      preloadRoute(anchor.pathname);
    };
    document.addEventListener('pointerover', onIntent, { passive: true });
    document.addEventListener('touchstart', onIntent, { passive: true });
    document.addEventListener('focusin', onIntent);
    return () => {
      document.removeEventListener('pointerover', onIntent);
      document.removeEventListener('touchstart', onIntent);
      document.removeEventListener('focusin', onIntent);
    };
  }, []);
}
